import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

const variants = {
  primary: `
    text-white shadow-btn-primary
    hover:shadow-btn-primary-hover hover:brightness-110
    active:brightness-95 active:shadow-btn-primary
    focus-visible:shadow-focus-ring
  `,
  secondary: `
    bg-white text-primary-900 border-[1.5px] border-primary-200 shadow-glass
    hover:bg-primary-50/60 hover:border-primary-300 hover:shadow-glass-hover
    active:bg-primary-100/60
    focus-visible:shadow-focus-ring
  `,
  outline: `
    bg-white text-surface-700 border-[1.5px] border-surface-200 shadow-glass
    hover:bg-surface-50 hover:border-surface-300 hover:shadow-glass-hover
    active:bg-surface-100
    focus-visible:shadow-focus-ring
  `,
  ghost: `
    text-surface-600
    hover:bg-surface-100/70 hover:text-surface-800
    active:bg-surface-200/50
    focus-visible:shadow-focus-ring
  `,
  danger: `
    text-white shadow-btn-danger
    hover:brightness-110
    active:brightness-95
    focus-visible:shadow-focus-danger
  `,
  'danger-outline': `
    bg-white text-danger-600 border-[1.5px] border-danger-200 shadow-glass
    hover:bg-danger-50/50 hover:border-danger-300
    active:bg-danger-100/50
    focus-visible:shadow-focus-danger
  `,
  accent: `text-white hover:brightness-110 active:brightness-95`,
  success: `text-white hover:brightness-110 active:brightness-95`,
};

const gradientStyles = {
  primary: { background: 'linear-gradient(135deg, #2a2a72, #1338be)' },
  danger: { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
  accent: { background: 'linear-gradient(135deg, #0891b2, #06b6d4)' },
  success: { background: 'linear-gradient(135deg, #16a34a, #22c55e)' },
};

const sizes = {
  xs: 'px-2.5 py-1 text-[11px] gap-1',
  sm: 'px-3 py-1.5 text-[12px] gap-1.5',
  md: 'px-4 py-2.5 text-[13px] gap-1.5',
  lg: 'px-5 py-2.5 text-sm gap-2',
  xl: 'px-6 py-3 text-sm gap-2',
};

const iconOnlySizes = {
  xs: 'p-1',
  sm: 'p-1.5',
  md: 'p-2.5',
  lg: 'p-2.5',
  xl: 'p-3',
};

const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      iconOnly = false,
      fullWidth = false,
      className = '',
      type = 'button',
      style: propStyle,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const sizeClass = iconOnly ? iconOnlySizes[size] : sizes[size];
    const iconSize = size === 'xs' ? 12 : size === 'sm' ? 13 : size === 'lg' || size === 'xl' ? 16 : 14;
    const inlineStyle = { ...propStyle, ...(gradientStyles[variant] || {}) };

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        style={inlineStyle}
        className={`
          inline-flex items-center justify-center font-semibold
          transition-all duration-200 ease-spring
          tap-highlight-none no-select rounded-[14px]
          ${sizeClass}
          ${variants[variant] || variants.primary}
          ${isDisabled ? 'opacity-45 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 size={iconSize} className="animate-spin flex-shrink-0" />
            {!iconOnly && <span>{children || 'Loading...'}</span>}
          </>
        ) : (
          <>
            {LeftIcon && <LeftIcon size={iconSize} className="flex-shrink-0" />}
            {!iconOnly && children}
            {RightIcon && <RightIcon size={iconSize} className="flex-shrink-0" />}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
export default Button;
